import React, { useState } from "react";
import { Star, StarBorder, Close } from "@mui/icons-material";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import GreenButton from "../GreenButton/GreenButton";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../utils/api";
import "./RatingComponent.scss";
import { toast } from "react-toastify";

const RatingComponent = ({ open, onClose, order, onRatingSubmitted }) => {
  const { language, t } = useLanguage();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Handle star click
  const handleStarClick = (starValue) => {
    setRating(starValue);
  };

  // Handle star hover
  const handleStarHover = (starValue) => {
    setHoveredRating(starValue);
  };

  // Handle mouse leave from stars
  const handleStarsMouseLeave = () => {
    setHoveredRating(0);
  };

  // Handle comment change
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  // Reset form
  const resetForm = () => {
    setRating(0);
    setHoveredRating(0);
    setComment("");
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle rating submission
  const handleSubmitRating = async () => {
    if (rating === 0) {
      setSubmitError(t("pleaseSelectRating") || "Please select a rating");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const ratingData = {
        comment: comment.trim(),
        rating: rating,
        technician: order.technician._id,
        bookingId: order._id,
      };

      await api.post("/ratings", ratingData);
      toast.success(t("successRating"));

      setSubmitSuccess(true);

      // Call the parent callback if provided
      if (onRatingSubmitted && typeof onRatingSubmitted === "function") {
        onRatingSubmitted(order._id, rating, comment);
      }

      // Close modal after a short delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error submitting rating:", error);
      setSubmitError(
        error.response?.data?.message ||
          t("ratingSubmissionError") ||
          "An error occurred while submitting your rating."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render star rating
  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isActive = i <= (hoveredRating || rating);
      stars.push(
        <button
          key={i}
          type="button"
          className={`star-button ${isActive ? "active" : ""}`}
          onClick={() => handleStarClick(i)}
          onMouseEnter={() => handleStarHover(i)}
          disabled={isSubmitting || submitSuccess}
        >
          {isActive ? (
            <Star className="star-icon filled" />
          ) : (
            <StarBorder className="star-icon empty" />
          )}
        </button>
      );
    }
    return stars;
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "90%",
      sm: "80%",
      md: 500,
      lg: 600,
    },
    maxWidth: 700,
    maxHeight: "90vh",
    overflow: "auto",
    bgcolor: "background.paper",
    border: "2px solid #5cb85c",
    borderRadius: "12px",
    boxShadow: 24,
    p: { xs: 2, sm: 3, md: 4 },
    direction: language === "ar" ? "rtl" : "ltr",
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="rating-modal-title"
      aria-describedby="rating-modal-description"
    >
      <Box sx={modalStyle}>
        <div
          className={`rating-component ${language === "ar" ? "rtl" : "ltr"}`}
        >
          {/* Header */}
          <div className="rating-header">
            <button
              type="button"
              className="close-button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <Close />
            </button>
          </div>

          {submitError && (
            <div className="error-message">
              <Typography variant="body2">{submitError}</Typography>
            </div>
          )}

          {!submitSuccess ? (
            <>
              {/* Technician Info */}
              <div className="technician-info">
                <Typography variant="body1" className="technician-name">
                  {t("whatCustomersSay") || "Rate"}{" "}
                </Typography>
              </div>

              {/* Star Rating */}
              <div className="star-rating-section">
                <Typography variant="body1" className="rating-label">
                  {t("selectRating") || "How would you rate our service?"}
                </Typography>
                <div
                  className="stars-container"
                  onMouseLeave={handleStarsMouseLeave}
                >
                  {renderStars()}
                </div>
              </div>

              {/* Comment Section */}
              <div className="comment-section">
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  label={t("addComment") || "Add a comment (optional)"}
                  value={comment}
                  onChange={handleCommentChange}
                  disabled={isSubmitting}
                  placeholder={
                    t("commentPlaceholder") ||
                    "Share your experience with us..."
                  }
                  className="comment-input"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#5cb85c",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#5cb85c",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#5cb85c",
                    },
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <GreenButton
                  text={t("cancel") || "Cancel"}
                  variant="outline"
                  size={language === "ar" ? "medium" : "large"}
                  onClick={handleClose}
                  disabled={isSubmitting}
                />
                <GreenButton
                  text={
                    isSubmitting
                      ? t("submitting") || "Submitting..."
                      : t("submitRating") || "Submit Rating"
                  }
                  variant="fill"
                  size={language === "ar" ? "medium" : "large"}
                  onClick={handleSubmitRating}
                  disabled={isSubmitting || rating === 0}
                />
              </div>
            </>
          ) : (
            /* Success Message */
            <div className="success-message">
              <div className="success-icon">
                <Star className="success-star" />
              </div>
              <Typography variant="body1" className="success-text">
                {t("ratingSubmittedMessage") ||
                  "Thank you for your feedback! Your rating has been submitted successfully."}
              </Typography>
              <Typography variant="body2" className="success-subtext">
                {t("ratingAppreciatedMessage") ||
                  "We appreciate your time and will use your feedback to improve our services."}
              </Typography>
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default RatingComponent;
